import Edit from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AddOnRateType, InvoiceLineItemType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { snakeCase } from 'lodash-es';
import { MouseEvent, ReactNode, useMemo, useState } from 'react';

import { Skeleton } from '~components/Skeleton/Skeleton';
import { AddOnCharge } from '~hooks/useAddOns/models';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { InvoiceLineItem } from '~hooks/useInvoiceLineItems/models';
import { Invoice } from '~hooks/useInvoices/models';
import { toDecimal } from '~utils/decimal';

import { TaxScheduleBadge } from './JobInvoiceEquation';

// Compact reconciliation, folded into the Summary's third column. The Receivable /
// Payable totals carry the visual weight; a small per-charge table lays the two
// sides side by side, with the per-side rate + billable quantity under each charge.
// Approve and Edit actions live in the Summary header (JobInvoiceActions).

const EM_DASH = '—';
const NO_CONTENT = '-';

// per-unit word for the "$25.25 / ton × 19.99" rate sub-line
const RATE_UNIT_PER: Record<string, string> = {
  RatePerBushel: 'bu',
  RatePerHour: 'hr',
  RatePerLoad: 'load',
  RatePerPound: 'lb',
  RatePerTon: 'ton',
  RatePerTonne: 'tonne',
  RatePerYard: 'yd',
};

interface BreakdownRow {
  key: string;
  label: ReactNode;
  ar: number | null;
  arSub?: ReactNode;
  ap: number | null;
  apSub?: ReactNode;
}

interface JobMarginBreakdownProps {
  companyId: string;
  payable: Invoice | null;
  receivable: Invoice | null;
  payableLineItems: InvoiceLineItem[];
  receivableLineItems: InvoiceLineItem[];
  payableAddOns?: AddOnCharge[];
  receivableAddOns?: AddOnCharge[];
  loadingInvoices?: boolean;
  // One-click edit per side — opens that invoice's editor drawer.
  onEditReceivable?: () => void;
  onEditPayable?: () => void;
}

export default function JobMarginBreakdown({
  companyId,
  payable,
  receivable,
  payableLineItems,
  receivableLineItems,
  payableAddOns = [],
  receivableAddOns = [],
  loadingInvoices,
  onEditReceivable,
  onEditPayable,
}: JobMarginBreakdownProps) {
  const theme = useTheme();
  const c = theme.brandV2.colors;
  const { currencyFormatter } = useCompanyCurrency();

  const { rows, totals } = useMemo(() => {
    const itemsOf = (items: InvoiceLineItem[], type: InvoiceLineItemType) =>
      items.filter((li) => li.lineItemType === type);
    const sum = (items: InvoiceLineItem[]) =>
      items
        .reduce((acc, li) => acc.plus(toDecimal(li.totalAmount)), toDecimal(0))
        .toNumber();

    const built: BreakdownRow[] = [];

    // "$25.25 / ton × 19.99 T" — the rate alongside the billable quantity it bills on
    const rateSub = (items: InvoiceLineItem[]) => {
      const rate = items[0]?.rate;
      const type = items[0]?.rateType;
      if (rate === null || rate === undefined) {
        return undefined;
      }
      const qty = items.reduce((acc, li) => acc + Number(li.quantity ?? 0), 0);
      const per =
        (type ? RATE_UNIT_PER[type] : undefined) ??
        (type ? t(`rate_types.${snakeCase(type)}`) : '');
      return `${currencyFormatter(Number(rate))}${per ? ` / ${per}` : ''} × ${qty.toFixed(2)}`;
    };

    // FREIGHT — rate × billable quantity, both shown on this row
    const arFreightItems = itemsOf(
      receivableLineItems,
      InvoiceLineItemType.FREIGHT_RATE_LINE_ITEM,
    );
    const apFreightItems = itemsOf(
      payableLineItems,
      InvoiceLineItemType.FREIGHT_RATE_LINE_ITEM,
    );
    const arFreight = arFreightItems.length ? sum(arFreightItems) : null;
    const apFreight = apFreightItems.length ? sum(apFreightItems) : null;
    if (arFreight !== null || apFreight !== null) {
      built.push({
        key: 'freight',
        label: t('approvals.approve_jobs.invoice_equation.freight'),
        ar: arFreight,
        arSub: arFreightItems.length ? rateSub(arFreightItems) : undefined,
        ap: apFreight,
        apSub: apFreightItems.length ? rateSub(apFreightItems) : undefined,
      });
    }

    // MATERIAL — commonly receivable-only; rate × billable quantity on this row too
    const arMaterialItems = itemsOf(
      receivableLineItems,
      InvoiceLineItemType.MATERIAL_RATE_LINE_ITEM,
    );
    const apMaterialItems = itemsOf(
      payableLineItems,
      InvoiceLineItemType.MATERIAL_RATE_LINE_ITEM,
    );
    const arMaterial = arMaterialItems.length ? sum(arMaterialItems) : null;
    const apMaterial = apMaterialItems.length ? sum(apMaterialItems) : null;
    if (arMaterial !== null || apMaterial !== null) {
      built.push({
        key: 'material',
        label: t('approvals.approve_jobs.invoice_equation.material'),
        ar: arMaterial,
        arSub: arMaterialItems.length ? rateSub(arMaterialItems) : undefined,
        ap: apMaterial,
        apSub: apMaterialItems.length ? rateSub(apMaterialItems) : undefined,
      });
    }

    // ADD-ONS — count + total per side; the count is a click-to-open popover
    // listing each add-on with its rate, quantity, and amount.
    const arAddOns = Number(receivable?.addOnChargesTotalAmount ?? 0);
    const apAddOns = Number(payable?.addOnChargesTotalAmount ?? 0);
    const arAddOnsCount = receivable?.addOnChargesCount ?? 0;
    const apAddOnsCount = payable?.addOnChargesCount ?? 0;
    if (arAddOnsCount > 0 || apAddOnsCount > 0) {
      const addOnSub = (count: number, charges: AddOnCharge[]) => {
        if (count <= 0) {
          return undefined;
        }
        if (!charges.length) {
          return t('approvals.approve_jobs.invoice_equation.add_on_count', { count });
        }
        return <AddOnsPopover count={count} charges={charges} />;
      };
      built.push({
        key: 'add_ons',
        label: t('approvals.approve_jobs.invoice_equation.add_ons'),
        ar: arAddOnsCount > 0 ? arAddOns : null,
        arSub: addOnSub(arAddOnsCount, receivableAddOns),
        ap: apAddOnsCount > 0 ? apAddOns : null,
        apSub: addOnSub(apAddOnsCount, payableAddOns),
      });
    }

    const commissionOf = (items: InvoiceLineItem[]) =>
      items
        .reduce((acc, li) => acc.plus(toDecimal(li.payCommissionAmount)), toDecimal(0))
        .toNumber();
    const arCommission = commissionOf(receivableLineItems);
    const apCommission = commissionOf(payableLineItems);
    if (arCommission !== 0 || apCommission !== 0) {
      built.push({
        key: 'commission',
        label: t('pay_commission_statement.deductions'),
        ar: arCommission || null,
        ap: apCommission || null,
      });
    }

    // TAX — receivable-only
    const tax = Number(receivable?.taxTotalAmount ?? 0);
    if (receivable && tax > 0) {
      const taxRate = Number(receivable.taxRate ?? 0);
      built.push({
        key: 'tax',
        label: (
          <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            {`${t('approvals.approve_jobs.invoice_equation.tax')} ${taxRate}%`}
            {receivable.externalTaxRateAccountingId && (
              <TaxScheduleBadge
                accountingId={receivable.externalTaxRateAccountingId}
                companyId={companyId}
              />
            )}
          </Box>
        ),
        ar: tax,
        ap: null,
      });
    }

    const arTotal = receivable ? Number(receivable.totalAmount ?? 0) : null;
    const apTotal = payable ? Number(payable.totalAmount ?? 0) : null;
    // Match the parent job-row Margin (MarginColumn): receivable total (incl. tax)
    // minus payable total, and only when BOTH invoices exist. Keeps the one Margin
    // figure consistent across the product; the canonical pre/post-tax basis is a
    // separate reports-audit decision.
    const marginTotal = arTotal !== null && apTotal !== null ? arTotal - apTotal : null;

    return { rows: built, totals: { ar: arTotal, ap: apTotal, margin: marginTotal } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    receivable,
    payable,
    receivableLineItems,
    payableLineItems,
    companyId,
    // Key on every field the AddOnsPopover renders (name + amount + rate/%/qty/type),
    // not just names, so an RTU refresh that changes amounts under the same names
    // recomputes the rows and the popover doesn't show stale line details.
    receivableAddOns
      .map(
        (a) =>
          `${a.name}:${a.totalAmount}:${a.rate}:${a.percentage}:${a.quantity}:${a.revenueCostRateType}`,
      )
      .join('|'),
    payableAddOns
      .map(
        (a) =>
          `${a.name}:${a.totalAmount}:${a.rate}:${a.percentage}:${a.quantity}:${a.revenueCostRateType}`,
      )
      .join('|'),
  ]);

  const signedMoney = (n: number) =>
    n < 0 ? `−${currencyFormatter(Math.abs(n))}` : `+${currencyFormatter(n)}`;

  const margin = totals.margin;
  const marginColor =
    margin === null ? c.treadGray5 : margin < 0 ? c.treadRed : c.treadGreen;

  // Per-side line items derived from the shared rows — each side lists only the
  // charges it actually carries (no em-dash placeholders in the statement).
  const receivableItems = rows
    .filter((row) => row.ar !== null)
    .map((row) => ({
      key: row.key,
      label: row.label,
      sub: row.arSub,
      value: row.ar as number,
    }));
  const payableItems = rows
    .filter((row) => row.ap !== null)
    .map((row) => ({
      key: row.key,
      label: row.label,
      sub: row.apSub,
      value: row.ap as number,
    }));

  if (loadingInvoices && !receivable && !payable) {
    return <BillingSkeleton />;
  }

  return (
    <Box>
      {/* Each side is a self-contained statement: line items, then a ruled TOTAL
          at the foot so the sum reads as belonging to the rows above it. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {totals.ar !== null && (
          <SideStatement
            title={t('approvals.approve_jobs.invoice_matrix.receivable')}
            items={receivableItems}
            total={totals.ar}
            format={currencyFormatter}
            onEdit={onEditReceivable}
          />
        )}
        {totals.ap !== null && (
          <SideStatement
            title={t('approvals.approve_jobs.invoice_matrix.payable')}
            items={payableItems}
            total={totals.ap}
            format={currencyFormatter}
            onEdit={onEditPayable}
          />
        )}
      </Box>

      {/* Margin — tucked at the bottom */}
      <Typography
        sx={{ fontSize: '12px', fontWeight: 600, color: c.treadGray2, mt: 1.5 }}
      >
        {t('approvals.approve_jobs.summary_hero.margin')}{' '}
        <Box component="span" sx={{ fontWeight: 800, color: marginColor }}>
          {loadingInvoices || margin === null ? NO_CONTENT : signedMoney(margin)}
        </Box>
      </Typography>
    </Box>
  );
}

interface StatementItem {
  key: string;
  label: ReactNode;
  sub?: ReactNode;
  value: number;
}

function BillingSkeleton() {
  const theme = useTheme();
  const c = theme.brandV2.colors;
  return (
    <Box data-test-id="billing-skeleton">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[0, 1].map((side) => (
          <Box key={side}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                pb: 0.5,
                mb: 0.25,
                borderBottom: `1.5px solid ${c.treadGray5}`,
              }}
            >
              <Skeleton variant="text" width={82} height={16} />
              <Skeleton variant="text" width={64} height={20} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 0.75 }}>
              {[0, 1].map((row) => (
                <Box
                  key={row}
                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
                >
                  <Skeleton variant="text" width={96} height={12} />
                  <Skeleton variant="text" width={52} height={12} />
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
      <Skeleton variant="text" width={128} height={14} sx={{ mt: 1.5 }} />
    </Box>
  );
}

// The "N add-ons" count: a click-to-open popover (like the tax-schedule badge)
// listing each add-on with its rate, quantity, and amount.
function AddOnsPopover({ count, charges }: { count: number; charges: AddOnCharge[] }) {
  const theme = useTheme();
  const c = theme.brandV2.colors;
  const { currencyFormatter } = useCompanyCurrency();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Percent add-ons carry their value in `percentage` and have no quantity;
  // per-unit add-ons carry it in `rate` and bill on a quantity.
  const subOf = (charge: AddOnCharge): string => {
    const rate = Number(charge.rate ?? 0);
    const pct = Number(charge.percentage ?? 0);
    const qty = Number(charge.quantity ?? 0);
    const isPercent =
      charge.revenueCostRateType === AddOnRateType.RATE_PERCENT_OF_TOTAL ||
      (!!pct && !rate);
    if (isPercent) {
      return pct ? `${pct}%` : EM_DASH;
    }
    if (!rate && !qty) {
      return EM_DASH;
    }
    return `${currencyFormatter(rate)} × ${qty.toFixed(2)}`;
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={(event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
        sx={{
          all: 'unset',
          cursor: 'pointer',
          color: c.treadGray2,
          borderBottom: `1px dashed ${c.treadGray5}`,
          '&:hover': { color: c.treadBlack },
        }}
      >
        {t('approvals.approve_jobs.invoice_equation.add_on_count', { count })}
      </Box>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClose={() => setAnchorEl(null)}
        open={Boolean(anchorEl)}
      >
        <Box
          sx={{ p: 1.5, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 1 }}
        >
          <Typography
            sx={{
              fontSize: '9px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '.06em',
              color: c.treadGray3,
            }}
          >
            {t('approvals.approve_jobs.invoice_equation.add_ons')}
          </Typography>
          {charges.map((charge, index) => (
            <Box
              key={`${charge.name}-${index}`}
              sx={{ display: 'flex', flexDirection: 'column', gap: 0.125 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                <Typography
                  sx={{ fontSize: '13px', fontWeight: 700, color: c.treadBlack }}
                >
                  {charge.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '13px',
                    fontWeight: 700,
                    color: c.treadBlack,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {currencyFormatter(Number(charge.totalAmount ?? 0))}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: '11px',
                  color: c.treadGray3,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {subOf(charge)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
}

function SideStatement({
  title,
  items,
  total,
  format,
  onEdit,
}: {
  title: string;
  items: StatementItem[];
  total: number | null;
  format: (amount: number) => string;
  onEdit?: () => void;
}) {
  const theme = useTheme();
  const c = theme.brandV2.colors;
  return (
    <Box>
      {/* Total heads the group — its line items sit indented beneath it. The
          label matches the other hero section labels. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          pb: 0.5,
          mb: 0.25,
          borderBottom: `1.5px solid ${c.treadGray5}`,
        }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 600, color: c.treadGray2 }}>
            {title}
          </Typography>
          {onEdit && (
            <Tooltip title={t('actions.edit')} placement="bottom">
              <IconButton
                aria-label={t('actions.edit')}
                onClick={onEdit}
                size="small"
                sx={{
                  p: 0.5,
                  border: `1px solid ${c.treadGray7}`,
                  borderRadius: '4px',
                  color: c.treadGray2,
                  '&:hover': {
                    borderColor: c.treadGray3,
                    backgroundColor: c.treadGray8,
                    color: c.treadBlack,
                  },
                }}
              >
                <Edit sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <TotalAmount value={total} format={format} />
      </Box>
      {items.length === 0 ? (
        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: c.treadGray5 }}>
          {EM_DASH}
        </Typography>
      ) : (
        items.map((item) => (
          <Box key={item.key} sx={{ py: 0.5, borderBottom: `1px solid ${c.treadGray8}` }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: c.treadGray2,
                  whiteSpace: 'nowrap',
                }}
              >
                {item.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12.5px',
                  fontWeight: 700,
                  color: c.treadBlack,
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
              >
                {format(item.value)}
              </Typography>
            </Box>
            {/* rate × qty on its own full-width line beneath the amount — no wrap */}
            {item.sub && (
              <Typography
                component="div"
                sx={{
                  fontSize: '10px',
                  fontWeight: 500,
                  color: c.treadGray3,
                  fontVariantNumeric: 'tabular-nums',
                  textAlign: 'right',
                  mt: '1px',
                }}
              >
                {item.sub}
              </Typography>
            )}
          </Box>
        ))
      )}
    </Box>
  );
}

function TotalAmount({
  value,
  format,
}: {
  value: number | null;
  format: (amount: number) => string;
}) {
  const theme = useTheme();
  const c = theme.brandV2.colors;
  return (
    <Typography
      component="span"
      sx={{
        fontSize: '24px',
        fontWeight: 700,
        lineHeight: 1.05,
        letterSpacing: '-.02em',
        color: value === null ? c.treadGray5 : c.treadBlack,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {value === null ? EM_DASH : format(value)}
    </Typography>
  );
}
